import { useEffect, type RefObject } from 'react';
import { startKeyboardFollow } from '@/lib/keyboard-follow';

/**
 * Scrolls an open picker dropdown clear of the on-screen keyboard, and caps the
 * list's height to whatever room is left above it.
 *
 * The motion and geometry live in `lib/keyboard-follow.ts` and
 * `lib/picker-keyboard-scroll.ts`, shared with the focused-field follow — a plain
 * input is the same problem with a zero-height list.
 *
 * Callers must pin the popover downward (`side="bottom" avoidCollisions={false}`):
 * the geometry always measures the list as hanging BELOW the trigger, so a
 * flipped list occupies space the scroll math still tries to clear.
 *
 * @param open       whether the picker is currently open
 * @param triggerRef the element the popover is anchored to. The scroll container
 *                   is resolved by walking up from HERE, not from the list, which
 *                   Radix portals out to `<body>`.
 * @param listRef    the popover content
 */
export function usePickerKeyboardScroll(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    const anchor = triggerRef.current;
    if (!anchor) return;
    // Read the list per frame rather than capturing it: Radix mounts the content
    // after the trigger's state flips, so it is still null right now.
    return startKeyboardFollow({ anchor, list: () => listRef.current });
  }, [open, triggerRef, listRef]);
}
